import { recordingAPI, transcriptionAPI } from '../services/api';
import { formatDateTime, formatDuration } from './transcriptionMeta';

const DONE = new Set(['stopped', 'uploaded', 'complete', 'certified', 'valid']);

const listOf = (resp) => {
  const data = resp?.data?.data ?? resp?.data;
  return Array.isArray(data) ? data : [];
};

const shortId = (value) => {
  const raw = String(value || '');
  if (!raw) return '—';
  return raw.length > 12 ? raw.slice(0, 8).toUpperCase() : raw;
};

export function mapCourtSession(row, transcript = null) {
  const id = row.session_id || row.id || row.transcription_id;
  const done = DONE.has(String(row.status || '').toLowerCase()) || Boolean(row.audio_asset_id);
  const when = row.started_at || row.stopped_at || row.created_at || row.recorded_at;
  return {
    id,
    session_id: shortId(id),
    case_number: row.case_reference || row.case_number || '—',
    case_title: row.case_reference || transcript?.title || row.language || 'Recording',
    recorded_at: formatDateTime(when) || '—',
    recorded_at_iso: when || '',
    duration: formatDuration(row.duration_seconds || transcript?.duration || transcript?.duration_seconds),
    status: done ? 'uploaded' : 'pending',
    transcription_id: transcript?.id || transcript?.transcription_id || row.transcription_id || null,
    audio_asset_id: row.audio_asset_id ? String(row.audio_asset_id) : null,
  };
}

export async function loadCourtSessions(tenantId) {
  if (!tenantId) return [];

  const [recResp, txResp] = await Promise.all([
    recordingAPI.listSessions(tenantId).catch(() => null),
    transcriptionAPI.getTranscriptions({ per_page: 200 }).catch(() => null),
  ]);

  const transcripts = listOf(txResp);
  const byAsset = new Map();
  transcripts.forEach((item) => {
    if (item.audio_asset_id) byAsset.set(String(item.audio_asset_id), item);
  });

  const usedAssets = new Set();
  const sessions = listOf(recResp).map((row) => {
    if (row.audio_asset_id) usedAssets.add(String(row.audio_asset_id));
    return mapCourtSession(row, byAsset.get(String(row.audio_asset_id)));
  });

  transcripts.forEach((item) => {
    const assetId = item.audio_asset_id ? String(item.audio_asset_id) : '';
    if (assetId && usedAssets.has(assetId)) return;
    if (assetId) usedAssets.add(assetId);
    sessions.push(mapCourtSession({
      id: item.id || item.transcription_id,
      case_reference: item.case_reference || item.title,
      created_at: item.created_at,
      status: item.status === 'uploading' ? 'pending' : 'uploaded',
      duration_seconds: item.duration || item.duration_seconds,
      audio_asset_id: item.audio_asset_id,
      transcription_id: item.id || item.transcription_id,
    }, item));
  });

  return sessions.sort((a, b) => String(b.recorded_at_iso).localeCompare(String(a.recorded_at_iso)));
}
